import { useState, useEffect } from "react";
import { imageService } from "../services/ImageService";

export interface CardImageState {
  imagePath: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * React hook for loading tarot card images with lazy loading
 * @param cardName - Name of the card to load
 * @param preload - Whether to preload the image immediately
 * @returns Object with image state
 */
export const useCardImage = (
  cardName: string | null,
  preload: boolean = true,
): CardImageState => {
  const [state, setState] = useState<CardImageState>({
    imagePath: null,
    isLoading: false,
    error: null,
  });

  useEffect(() => {
    if (!cardName) {
      setState({ imagePath: null, isLoading: false, error: null });
      return;
    }

    if (preload) {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      imageService
        .preloadCard(cardName)
        .then((imagePath) => {
          setState({ imagePath, isLoading: false, error: null });
        })
        .catch((error) => {
          setState({
            imagePath: null,
            isLoading: false,
            error: error.message || "Failed to load image",
          });
        });
    } else {
      // Just get the path without loading
      const imagePath = imageService.getCardImagePath(cardName);
      setState({ imagePath, isLoading: false, error: null });
    }
  }, [cardName, preload]);

  return state;
};
